import React from 'react'
import { useDerivedMintInfo, useMintActionHandlers, useMintState } from '../../../../state/mint/hooks'
import { ETHER, TokenAmount } from '@uniswap/sdk-core'
import { Field } from '../../../../state/mint/actions'
import { maxAmountSpend } from '../../../../utils/maxAmountSpend'
import { useActiveWeb3React } from '../../../../hooks'
import { useWalletModalToggle } from '../../../../state/application/hooks'
import { ButtonLight, ButtonPrimary } from '../../../../components/Button'
import { RowBetween } from '../../../../components/Row'
import { useTransactionAdder } from '../../../../state/transactions/hooks'
import { parseUnits } from 'ethers/lib/utils'
import { useETHBalances } from '../../../../state/wallet/hooks'
import { calculateGasMargin, getContract } from '../../../../utils'
import { Text } from 'rebass'
import { STHYPE_OVERSEER_ADDRESS } from '@hyperswap/addresses'
import { abi as STHypeOverseerABI } from '../../../../abis/sthype_overseer.json'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import styled from 'styled-components'

interface StakeProps {
  key?: number
  setRefresh: () => void
}

export const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

const Stake: React.FC<StakeProps> = ({ setRefresh }) => {
  const { independentField, typedValue, otherTypedValue } = useMintState()
  const { account, chainId, library } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const ethBalance = useETHBalances(account ? [account as any] : undefined)

  const [loadingAdd, setLoadingAdd] = React.useState<boolean>(false)
  const {
    dependentField,
    currencies,

    currencyBalances,
    parsedAmounts,

    noLiquidity,
  } = useDerivedMintInfo(ETHER ?? undefined, undefined)

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: noLiquidity ? otherTypedValue : parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const { onFieldAInput } = useMintActionHandlers(noLiquidity)

  const maxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const atMaxAmounts: { [field in Field]?: TokenAmount } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmounts[field]?.equalTo(parsedAmounts[field] ?? '0'),
      }
    },
    {}
  )
  const addTransaction = useTransactionAdder()

  async function onAdd() {
    if (!chainId || !library || !account) return
    const overseerV1Contract = getContract(STHYPE_OVERSEER_ADDRESS, STHypeOverseerABI, library, account)
    const estimate = overseerV1Contract.estimateGas.mint
    const method = overseerV1Contract.mint

    const value = parseUnits(`${parsedAmounts[Field.CURRENCY_A]?.toExact()}`, 18).toString()
    const args = [account]
    setLoadingAdd(true)
    await estimate(...args, {
      ...(value ? { value } : {}),
    })
      .then((estimatedGasLimit: any) => {
        return method(...args, {
          gasLimit: calculateGasMargin(estimatedGasLimit),
          ...(value ? { value } : {}),
        }).then(async (response: any) => {
          addTransaction(response, {
            summary: `Stake HYPE`,
          })

          // Wait for the transaction to be mined
          await response.wait()
          setRefresh()
          setLoadingAdd(false)
        })
      })
      .catch((error) => {
        setLoadingAdd(false)
        console.error(error)
      })
  }

  const isDisabled: boolean =
    loadingAdd === true ||
    formattedAmounts[Field.CURRENCY_A].length === 0 ||
    parseFloat(formattedAmounts[Field.CURRENCY_A]) <= 0 ||
    ((ethBalance &&
      account &&
      ethBalance[account] &&
      parseFloat(formattedAmounts[Field.CURRENCY_A]) >
        parseFloat(ethBalance?.[account as any]?.toExact() as any)) as boolean)

  return (
    <ContainerStyled>
      <CurrencyInputPanel
        value={formattedAmounts[Field.CURRENCY_A]}
        onUserInput={onFieldAInput}
        onMax={() => {
          onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact() ?? '')
        }}
        showMaxButton={!atMaxAmounts[Field.CURRENCY_A]}
        currency={currencies[Field.CURRENCY_A]}
        id="stakehype-earn-sthype-stake"
        showCommonBases
        smallTokenName
      />

      <div style={{ position: 'relative', marginTop: 'auto' }}>
        {!account ? (
          <ButtonLight onClick={toggleWalletModal}>Connect Wallet</ButtonLight>
        ) : (
          <RowBetween>
            <ButtonPrimary
              id="join-pool-button"
              style={{ padding: 16 }}
              as={'button'}
              onClick={() => onAdd()}
              disabled={isDisabled}
            >
              <Text fontWeight={400} fontSize={14} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {loadingAdd ? 'Staking...' : 'Stake'}
              </Text>
            </ButtonPrimary>
          </RowBetween>
        )}
      </div>
    </ContainerStyled>
  )
}
export default Stake
